import React, { useState, useEffect } from 'react';
import { apiClient, useAuth } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Phone, Brain, FileText, Clock, User, TrendingUp,
  Mic, BarChart3, AlertTriangle, CheckCircle, XCircle, RefreshCw,
  ThumbsUp, ThumbsDown, Minus, Loader2, Search, Play,
} from 'lucide-react';

const STATUS_COLORS = {
  pending_transcription: 'bg-yellow-500/10 text-yellow-600',
  downloaded: 'bg-yellow-500/10 text-yellow-600',
  transcribed: 'bg-blue-500/10 text-blue-600',
  analyzed: 'bg-green-500/10 text-green-600',
  transcription_failed: 'bg-red-500/10 text-red-600',
  no_recording: 'bg-slate-500/10 text-slate-500',
};

const STATUS_LABELS = {
  no_recording: 'No Audio',
  transcription_failed: 'Failed — Auto-retry',
  pending_transcription: 'Queued',
  downloaded: 'Queued',
};

const SENTIMENT_ICONS = {
  positive: { icon: ThumbsUp, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  neutral: { icon: Minus, color: 'text-gray-500', bg: 'bg-gray-500/10' },
  negative: { icon: ThumbsDown, color: 'text-red-500', bg: 'bg-red-500/10' },
};

function formatDuration(seconds) {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function CallIntelligencePage() {
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, call: null, loading: false });
  const [processing, setProcessing] = useState({});
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [queueStatus, setQueueStatus] = useState(null);
  // Sentiment drill-down dialog: { open, sentiment: 'positive'|'neutral'|'negative', agentFilter: 'all'|agentId }
  const [sentimentDrill, setSentimentDrill] = useState({ open: false, sentiment: null, agentFilter: 'all' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  // Poll queue health every 30s so the header badge stays fresh
  useEffect(() => {
    let cancelled = false;
    const fetchQueue = async () => {
      try {
        const r = await apiClient.get('/calls/queue-status');
        if (!cancelled) setQueueStatus(r.data);
      } catch { /* silent — badge just won't update */ }
    };
    fetchQueue();
    const t = setInterval(fetchQueue, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [callsRes, analyticsRes] = await Promise.all([
        // Pull ALL calls with lightweight projection — Claret's Learning Engine needs
        // full history. `light=true` returns only list-view fields (id, agent, lead,
        // status, duration, sentiment, score) — ~5x smaller payload. Click a row to
        // fetch the full detail (transcript + analysis) via GET /calls/{id}.
        apiClient.get('/calls?limit=10000&light=true'),
        apiClient.get('/calls/analytics').catch(() => ({ data: null })),
      ]);
      setCalls(callsRes.data || []);
      setAnalytics(analyticsRes.data);
    } catch { toast.error('Failed to load calls'); }
    setLoading(false);
  }

  async function handleSync() {
    setSyncing(true);
    try {
      await apiClient.post('/3cx/sync-now');
      toast.success('3CX sync triggered');
      setTimeout(fetchData, 5000);
    } catch { toast.error('Sync failed'); }
    setSyncing(false);
  }

  async function handleTranscribe(callId) {
    setProcessing(p => ({ ...p, [callId]: 'transcribing' }));
    try {
      await apiClient.post(`/calls/${callId}/transcribe`, {}, { timeout: 180000 });
      toast.success('Transcription complete');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transcription failed');
    } finally {
      setProcessing(p => ({ ...p, [callId]: null }));
    }
  }

  async function handleAnalyze(callId) {
    setProcessing(p => ({ ...p, [callId]: 'analyzing' }));
    try {
      await apiClient.post(`/calls/${callId}/analyze`, {}, { timeout: 120000 });
      toast.success('Analysis complete');
      fetchData();
      // Refresh detail if open
      if (detailDialog.open && detailDialog.call?.id === callId) {
        const res = await apiClient.get(`/calls/${callId}`);
        setDetailDialog(prev => ({ ...prev, call: res.data }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Analysis failed — try again in a moment');
    } finally {
      setProcessing(p => ({ ...p, [callId]: null }));
    }
  }

  async function handleBulkAnalyze() {
    const pending = calls.filter(c => c.status === 'transcribed');
    if (!pending.length) { toast.info('No calls to analyze'); return; }
    setBulkAnalyzing(true);
    toast.info(`Analyzing ${pending.length} calls... This may take a few minutes.`);
    let done = 0;
    for (const call of pending) {
      try {
        await apiClient.post(`/calls/${call.id}/analyze`, {}, { timeout: 120000 });
        done++;
      } catch { /* continue */ }
    }
    toast.success(`Analyzed ${done}/${pending.length} calls`);
    setBulkAnalyzing(false);
    fetchData();
  }

  async function viewDetail(call) {
    setDetailDialog({ open: true, call: null, loading: true });
    try {
      const res = await apiClient.get(`/calls/${call.id}`);
      setDetailDialog({ open: true, call: res.data, loading: false });
    } catch {
      setDetailDialog(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load details');
    }
  }

  const filteredCalls = calls.filter(c => {
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    if (filterAgent !== 'all' && (c.agent_id || '__none__') !== filterAgent) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (c.agent_name || '').toLowerCase().includes(q) || (c.lead_name || '').toLowerCase().includes(q) || (c.phone_number || '').includes(q);
    }
    return true;
  });

  // Build agent list from the calls themselves so unassigned calls surface too
  const agentOptions = (() => {
    const map = new Map();
    calls.forEach(c => {
      const id = c.agent_id || '__none__';
      const name = c.agent_name || 'Unassigned';
      if (!map.has(id)) map.set(id, { id, name, count: 0 });
      map.get(id).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.id === '__none__') return 1;  // unassigned last
      if (b.id === '__none__') return -1;
      return (a.name || '').localeCompare(b.name || '');
    });
  })();

  const summary = {
    total: calls.length,
    transcribed: calls.filter(c => c.status === 'transcribed').length,
    analyzed: calls.filter(c => c.status === 'analyzed').length,
    pending: calls.filter(c => ['pending_transcription', 'downloaded'].includes(c.status)).length,
  };

  // Sentiment breakdown across analyzed calls (drill-down source for the
  // Overall Sentiment card). Only counts calls that actually have an
  // analysis.sentiment — i.e. status === 'analyzed'.
  const analyzedCalls = calls.filter(c => c.status === 'analyzed' && c.analysis?.sentiment?.overall);
  const sentimentSummary = {
    total: analyzedCalls.length,
    positive: analyzedCalls.filter(c => c.analysis.sentiment.overall === 'positive').length,
    neutral: analyzedCalls.filter(c => c.analysis.sentiment.overall === 'neutral').length,
    negative: analyzedCalls.filter(c => c.analysis.sentiment.overall === 'negative').length,
  };

  // Per-agent positive % → indicator dot in the agent filter dropdown.
  // Computed only from calls that have a sentiment, so unanalyzed agents don't
  // get a false red.
  const agentSentimentStats = (() => {
    const map = new Map();
    analyzedCalls.forEach(c => {
      const id = c.agent_id || '__none__';
      if (!map.has(id)) map.set(id, { total: 0, positive: 0 });
      const slot = map.get(id);
      slot.total += 1;
      if (c.analysis.sentiment.overall === 'positive') slot.positive += 1;
    });
    return map;
  })();

  const agentDotColor = (agentId) => {
    const stats = agentSentimentStats.get(agentId);
    if (!stats || stats.total === 0) return 'bg-slate-300 dark:bg-slate-600';
    const pct = (stats.positive / stats.total) * 100;
    if (pct >= 20) return 'bg-emerald-500';
    if (pct >= 10) return 'bg-amber-400';
    return 'bg-red-500';
  };

  const agentPositivePct = (agentId) => {
    const stats = agentSentimentStats.get(agentId);
    if (!stats || stats.total === 0) return null;
    return Math.round((stats.positive / stats.total) * 100);
  };

  const SentimentBadge = ({ sentiment }) => {
    if (!sentiment) return null;
    const overall = sentiment.overall || 'neutral';
    const config = SENTIMENT_ICONS[overall] || SENTIMENT_ICONS.neutral;
    const Icon = config.icon;
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
        <Icon className="h-3 w-3" />
        {overall}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="call-intelligence-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Call Intelligence</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-muted-foreground text-sm">AI-powered call analysis and coaching</p>
            {queueStatus && (() => {
              const { pending = 0, awaiting_analysis = 0, retrying = 0, analyzed_24h = 0 } = queueStatus;
              const inFlight = pending + awaiting_analysis + retrying;
              const dot = inFlight === 0
                ? { color: 'bg-emerald-500', pulse: '' }
                : retrying > 0
                  ? { color: 'bg-red-500', pulse: 'animate-pulse' }
                  : { color: 'bg-amber-500', pulse: 'animate-pulse' };
              const parts = [];
              if (pending) parts.push(`${pending} queued`);
              if (awaiting_analysis) parts.push(`${awaiting_analysis} analyzing`);
              if (retrying) parts.push(`${retrying} retrying`);
              const tooltip = inFlight === 0
                ? `All caught up · ${analyzed_24h} analyzed in last 24h`
                : `Auto-analysis: ${parts.join(' · ')} · ${analyzed_24h} done in last 24h`;
              return (
                <span
                  className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border bg-muted/40"
                  title={tooltip}
                  data-testid="call-queue-status-badge"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${dot.color} ${dot.pulse}`} />
                  {inFlight === 0
                    ? <span className="text-muted-foreground">Auto-analysis live</span>
                    : <span className="text-muted-foreground">{parts.join(' · ')}</span>}
                </span>
              );
            })()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-3cx-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync 3CX
          </Button>
          {summary.transcribed > 0 && (
            <Button size="sm" onClick={handleBulkAnalyze} disabled={bulkAnalyzing} data-testid="bulk-analyze-btn">
              {bulkAnalyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Brain className="h-4 w-4 mr-1" />}
              Analyze All ({summary.transcribed})
            </Button>
          )}
        </div>
      </div>

      {/* Overall Sentiment Summary — clickable drill-down */}
      <Card data-testid="overall-sentiment-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Overall Call Sentiment</p>
              <p className="text-4xl font-bold mt-1" data-testid="sentiment-total">{sentimentSummary.total.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Total calls analyzed</p>
            </div>
            <div className="flex items-stretch gap-2">
              {[
                { key: 'positive', label: 'Positive', count: sentimentSummary.positive, color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/20', dot: 'bg-emerald-500' },
                { key: 'neutral',  label: 'Neutral',  count: sentimentSummary.neutral,  color: 'bg-slate-500/10 text-slate-600 border-slate-500/30 hover:bg-slate-500/20',     dot: 'bg-slate-400' },
                { key: 'negative', label: 'Negative', count: sentimentSummary.negative, color: 'bg-red-500/10 text-red-600 border-red-500/30 hover:bg-red-500/20',         dot: 'bg-red-500' },
              ].map(s => {
                const pct = sentimentSummary.total ? Math.round((s.count / sentimentSummary.total) * 100) : 0;
                return (
                  <button
                    key={s.key}
                    onClick={() => setSentimentDrill({ open: true, sentiment: s.key, agentFilter: 'all' })}
                    disabled={s.count === 0}
                    className={`min-w-[120px] px-4 py-3 rounded-lg border transition-all text-left ${s.color} ${s.count === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`sentiment-drill-${s.key}`}
                    title={s.count === 0 ? `No ${s.label.toLowerCase()} calls yet` : `Click to drill down into ${s.count} ${s.label.toLowerCase()} calls`}
                  >
                    <div className="flex items-center gap-1.5 text-[11px] uppercase font-medium">
                      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                      {s.label}
                    </div>
                    <div className="flex items-baseline gap-1.5 mt-1">
                      <span className="text-2xl font-bold">{s.count.toLocaleString()}</span>
                      <span className="text-xs">{pct}%</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold">{summary.total}</p>
          <p className="text-xs text-muted-foreground">Total Calls</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-yellow-500">{summary.pending}</p>
          <p className="text-xs text-muted-foreground">Pending</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-blue-500">{summary.transcribed}</p>
          <p className="text-xs text-muted-foreground">Transcribed</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-emerald-500">{summary.analyzed}</p>
          <p className="text-xs text-muted-foreground">Analyzed</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search agent, lead, phone..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 h-9 text-sm" data-testid="call-search" />
        </div>
        <Select value={filterAgent} onValueChange={setFilterAgent}>
          <SelectTrigger className="w-56 h-9 text-xs" data-testid="call-agent-filter">
            <SelectValue placeholder="All agents" />
          </SelectTrigger>
          <SelectContent className="max-h-96">
            <SelectItem value="all">All agents ({calls.length})</SelectItem>
            {agentOptions.map(a => {
              const pct = agentPositivePct(a.id);
              return (
                <SelectItem key={a.id} value={a.id} data-testid={`agent-opt-${a.id}`}>
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full flex-shrink-0 ${agentDotColor(a.id)}`}
                      title={pct === null ? 'No analyzed calls yet' : `${pct}% positive calls`}
                    />
                    <span className="truncate">{a.name}</span>
                    <span className="text-muted-foreground">({a.count}{pct !== null ? ` · ${pct}%+` : ''})</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 h-9 text-xs" data-testid="call-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="transcribed">Transcribed</SelectItem>
            <SelectItem value="analyzed">Analyzed</SelectItem>
            <SelectItem value="pending_transcription">Pending</SelectItem>
          </SelectContent>
        </Select>
        {(filterAgent !== 'all' || filterStatus !== 'all' || searchTerm) && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs" data-testid="filter-result-count">
              {filteredCalls.length} / {calls.length}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-2 text-xs"
              onClick={() => { setFilterAgent('all'); setFilterStatus('all'); setSearchTerm(''); }}
              data-testid="clear-filters-btn"
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      {/* Call List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : filteredCalls.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">No calls found</div>
          ) : (
            <div className="divide-y">
              {filteredCalls.map((call, i) => {
                const sentiment = call.analysis?.sentiment;
                return (
                  <div key={call.id} className="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => viewDetail(call)} data-testid={`call-row-${i}`}>
                    <div className="flex-shrink-0">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${call.call_type === 'inbound' ? 'bg-blue-500/10' : 'bg-emerald-500/10'}`}>
                        <Phone className={`h-5 w-5 ${call.call_type === 'inbound' ? 'text-blue-500' : 'text-emerald-500'}`} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{call.lead_name || call.phone_number || 'Unknown'}</p>
                        <Badge className={STATUS_COLORS[call.status] || 'bg-muted'} variant="outline">
                          {STATUS_LABELS[call.status] || call.status?.replace(/_/g, ' ')}
                        </Badge>
                        {sentiment && <SentimentBadge sentiment={sentiment} />}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><User className="h-3 w-3" />{call.agent_name || 'Unknown'}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDuration(call.call_duration_seconds)}</span>
                        <span>{call.created_at ? new Date(call.created_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        {call.status === 'no_recording' && (
                          <span className="text-slate-500 italic truncate" title={call.error}>No audio attached</span>
                        )}
                      </div>
                    </div>
                    {call.analysis?.agent_performance?.score !== undefined && (
                      <div className="text-center flex-shrink-0">
                        <p className={`text-lg font-bold ${call.analysis.agent_performance.score >= 70 ? 'text-emerald-600' : call.analysis.agent_performance.score >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
                          {call.analysis.agent_performance.score}
                        </p>
                        <p className="text-[10px] text-muted-foreground">Score</p>
                      </div>
                    )}
                    <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      {['pending_transcription', 'downloaded'].includes(call.status) && (
                        <Button size="sm" variant="outline" onClick={() => handleTranscribe(call.id)} disabled={!!processing[call.id]}>
                          <Mic className="h-3 w-3 mr-1" /> {processing[call.id] === 'transcribing' ? '...' : 'Transcribe'}
                        </Button>
                      )}
                      {call.status === 'transcribed' && (
                        <Button size="sm" variant="outline" onClick={() => handleAnalyze(call.id)} disabled={!!processing[call.id]}>
                          <Brain className="h-3 w-3 mr-1" /> {processing[call.id] === 'analyzing' ? 'Analyzing...' : 'Analyze'}
                        </Button>
                      )}
                      {call.status === 'transcription_failed' && (
                        <Button size="sm" variant="outline" onClick={() => handleTranscribe(call.id)} disabled={!!processing[call.id]} title={call.error || 'Retry transcription'}>
                          <RefreshCw className="h-3 w-3 mr-1" /> Retry
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sentiment Drill-down Dialog */}
      <Dialog open={sentimentDrill.open} onOpenChange={o => { if (!o) setSentimentDrill(s => ({ ...s, open: false })); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="sentiment-drill-dialog">
          {(() => {
            const s = sentimentDrill.sentiment;
            const labelMap = { positive: 'Positive', neutral: 'Neutral', negative: 'Negative' };
            const colorMap = { positive: 'text-emerald-600', neutral: 'text-slate-600', negative: 'text-red-600' };
            const dotMap = { positive: 'bg-emerald-500', neutral: 'bg-slate-400', negative: 'bg-red-500' };
            const drillRows = analyzedCalls.filter(c => c.analysis.sentiment.overall === s);
            const filteredDrill = sentimentDrill.agentFilter === 'all'
              ? drillRows
              : drillRows.filter(c => (c.agent_id || '__none__') === sentimentDrill.agentFilter);

            // Per-agent counts for the agent dropdown inside the dialog —
            // limited to this sentiment slice.
            const drillAgents = (() => {
              const m = new Map();
              drillRows.forEach(c => {
                const id = c.agent_id || '__none__';
                const name = c.agent_name || 'Unassigned';
                if (!m.has(id)) m.set(id, { id, name, count: 0 });
                m.get(id).count += 1;
              });
              return Array.from(m.values()).sort((a, b) => b.count - a.count);
            })();

            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${dotMap[s] || 'bg-slate-400'}`} />
                    <span className={colorMap[s] || ''}>{labelMap[s] || 'Calls'}</span>
                    <span className="text-muted-foreground font-normal">· {filteredDrill.length} of {drillRows.length}</span>
                  </DialogTitle>
                  <DialogDescription>
                    Drill into every {(labelMap[s] || '').toLowerCase()} call. Filter by agent below.
                  </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <Select
                    value={sentimentDrill.agentFilter}
                    onValueChange={v => setSentimentDrill(prev => ({ ...prev, agentFilter: v }))}
                  >
                    <SelectTrigger className="w-64 h-9 text-xs" data-testid="drill-agent-filter">
                      <SelectValue placeholder="All agents" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectItem value="all">All agents ({drillRows.length})</SelectItem>
                      {drillAgents.map(a => (
                        <SelectItem key={a.id} value={a.id} data-testid={`drill-agent-${a.id}`}>
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full flex-shrink-0 ${agentDotColor(a.id)}`} />
                            <span className="truncate">{a.name}</span>
                            <span className="text-muted-foreground">({a.count})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="divide-y border rounded-md max-h-[55vh] overflow-y-auto">
                  {filteredDrill.length === 0 ? (
                    <div className="text-center py-10 text-sm text-muted-foreground">No calls match this filter.</div>
                  ) : filteredDrill.map((c, i) => (
                    <div
                      key={c.id}
                      className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors cursor-pointer text-sm"
                      onClick={() => { setSentimentDrill(prev => ({ ...prev, open: false })); viewDetail(c); }}
                      data-testid={`drill-row-${i}`}
                    >
                      <Phone className={`h-4 w-4 flex-shrink-0 ${c.call_type === 'inbound' ? 'text-blue-500' : 'text-emerald-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{c.lead_name || c.phone_number || 'Unknown'}</span>
                          {c.analysis?.agent_performance?.score !== undefined && (
                            <Badge variant="outline" className="text-[10px]">
                              {c.analysis.agent_performance.score}/100
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          <span className="flex items-center gap-1"><User className="h-3 w-3" />{c.agent_name || 'Unknown'}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDuration(c.call_duration_seconds)}</span>
                          <span>{c.created_at ? new Date(c.created_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' }) : ''}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={o => { if (!o) setDetailDialog(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="call-detail-dialog">
          {detailDialog.loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : detailDialog.call && (() => {
            const c = detailDialog.call;
            const a = c.analysis;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-emerald-500" />
                    {c.lead_name || c.phone_number || 'Call Detail'}
                  </DialogTitle>
                  <DialogDescription className="flex items-center gap-3">
                    <span>{c.agent_name}</span>
                    <span>{c.call_type}</span>
                    <span>{formatDuration(c.call_duration_seconds)}</span>
                    {a?.sentiment && <SentimentBadge sentiment={a.sentiment} />}
                    {c.status === 'transcribed' && !a && (
                      <Button size="sm" onClick={() => handleAnalyze(c.id)} disabled={!!processing[c.id]} data-testid="detail-analyze-btn">
                        {processing[c.id] === 'analyzing' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Brain className="h-3 w-3 mr-1" />}
                        Analyze Now
                      </Button>
                    )}
                  </DialogDescription>
                </DialogHeader>

                {/* Analysis */}
                {a && (
                  <div className="space-y-4">
                    {/* Summary + Sentiment */}
                    <Card className="border-emerald-500/20 bg-emerald-500/5">
                      <CardContent className="py-4 space-y-3">
                        <p className="text-sm">{a.summary}</p>
                        <div className="flex gap-2 flex-wrap">
                          <Badge>Intent: {a.buying_intent_score}/100</Badge>
                          <Badge variant="outline">Customer: {a.sentiment?.customer_tone}</Badge>
                          <Badge variant="outline">Agent: {a.sentiment?.agent_tone}</Badge>
                          <Badge variant="outline">Trust: {a.trust_level}</Badge>
                          <Badge variant="outline">Urgency: {a.urgency_level}</Badge>
                          <Badge variant="outline">Next: {a.recommended_next_action?.replace(/_/g, ' ')}</Badge>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Agent Score */}
                    {a.agent_performance && (
                      <Card>
                        <CardHeader className="pb-2"><CardTitle className="text-sm">Agent Performance</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center gap-3">
                            <span className="text-sm w-20">Score:</span>
                            <Progress value={a.agent_performance.score} className="flex-1" />
                            <span className="font-bold">{a.agent_performance.score}/100</span>
                          </div>
                          {a.agent_performance.strengths?.length > 0 && (
                            <div><p className="text-xs font-medium text-emerald-600 mb-1">Strengths:</p>
                              {a.agent_performance.strengths.map((s, i) => (
                                <p key={`str-${i}`} className="text-sm flex items-start gap-1"><CheckCircle className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />{s}</p>
                              ))}
                            </div>
                          )}
                          {a.agent_performance.weaknesses?.length > 0 && (
                            <div><p className="text-xs font-medium text-red-600 mb-1">Areas to Improve:</p>
                              {a.agent_performance.weaknesses.map((w, i) => (
                                <p key={`weak-${i}`} className="text-sm flex items-start gap-1"><XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />{w}</p>
                              ))}
                            </div>
                          )}
                          {a.coaching_tips?.length > 0 && (
                            <div><p className="text-xs font-medium text-blue-600 mb-1">Coaching Tips:</p>
                              {a.coaching_tips.map((t, i) => (
                                <p key={`tip-${i}`} className="text-sm flex items-start gap-1"><TrendingUp className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />{t}</p>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Objections */}
                    {a.objections?.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2"><CardTitle className="text-sm">Objections ({a.objections.length})</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                          {a.objections.map((obj, i) => (
                            <div key={`obj-${i}`} className="p-3 rounded-lg border space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{obj.type}</Badge>
                                {obj.handled_well ? <Badge className="bg-emerald-500/10 text-emerald-600">Handled Well</Badge> : <Badge className="bg-red-500/10 text-red-600">Needs Work</Badge>}
                              </div>
                              <p className="text-sm italic">"{obj.text}"</p>
                              {obj.better_response && <p className="text-xs text-muted-foreground">Suggested: {obj.better_response}</p>}
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Transcript — always shown if available */}
                {c.transcript && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Transcript</CardTitle></CardHeader>
                    <CardContent>
                      <ScrollArea className="max-h-[300px]">
                        {c.transcript.segments?.length > 0 ? (
                          <div className="space-y-1.5">
                            {c.transcript.segments.map((seg, i) => (
                              <div key={`seg-${i}`} className="flex gap-2 text-sm">
                                <span className="text-xs text-muted-foreground w-12 flex-shrink-0 pt-0.5 font-mono">{formatDuration(seg.start)}</span>
                                {seg.speaker && <span className="text-xs font-medium text-blue-500 w-16 flex-shrink-0">{seg.speaker}</span>}
                                <p className="flex-1">{seg.text}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{c.transcript.full_text}</p>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {/* No transcript yet */}
                {!c.transcript && (
                  <div className="text-center py-6 text-muted-foreground">
                    <Mic className="h-8 w-8 mx-auto mb-2" />
                    <p className="text-sm">No transcript available yet</p>
                    {['pending_transcription', 'downloaded'].includes(c.status) && (
                      <Button size="sm" className="mt-2" onClick={() => handleTranscribe(c.id)} disabled={!!processing[c.id]}>
                        <Mic className="h-3 w-3 mr-1" /> Transcribe Now
                      </Button>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
